import { Close } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import useDialog from '../../../../hooks/useDialog';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';

const GROUP_META = [
    { key: 'payments', label: 'Cobranças', description: 'Eventos PAYMENT_*' },
    { key: 'subscriptions', label: 'Assinaturas', description: 'Eventos SUBSCRIPTION_*' },
    { key: 'checkout', label: 'Checkout', description: 'Eventos CHECKOUT_*' },
];

function Edit({ id, mutate, catalog }) {
    const { data } = useFetch(id ? `settings/asaas-webhooks/${id}` : null);
    return <Form data={data} mutate={mutate} catalog={catalog} />;
}

function Form({ data, mutate, catalog }) {
    const { open, handleClose } = useDialog('#webhook');
    const { setErrors, enqueueSnackbar } = useErrors();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [expandedGroup, setExpandedGroup] = useState('payments');

    const groups = catalog?.groups || {
        payments: [],
        subscriptions: [],
        checkout: [],
    };
    const defaultUrl = catalog?.defaultUrl || '';

    const { values, handleSubmit, setValues, handleChange, isSubmitting, setFieldValue } = useFormik({
        initialValues: {
            id: '',
            name: 'Eurobem webhook',
            url: defaultUrl,
            email: '',
            enabled: true,
            interrupted: false,
            sendType: 'NON_SEQUENTIALLY',
            authToken: '',
            events: [],
        },
        onSubmit: async (vals, { setSubmitting }) => {
            try {
                if (!vals.events?.length) {
                    enqueueSnackbar('Selecione ao menos um evento.', { variant: 'warning' });
                    setSubmitting(false);
                    return;
                }
                const payload = {
                    name: vals.name?.trim() || 'Eurobem webhook',
                    url: vals.url?.trim(),
                    email: vals.email?.trim() || undefined,
                    enabled: Boolean(vals.enabled),
                    interrupted: Boolean(vals.interrupted),
                    sendType: vals.sendType,
                    events: vals.events,
                };
                if (vals.authToken?.trim()) {
                    payload.authToken = vals.authToken.trim();
                }

                if (vals.id) {
                    await server.put(`settings/asaas-webhooks/${vals.id}`, payload);
                    enqueueSnackbar('Webhook atualizado.');
                } else {
                    await server.post('settings/asaas-webhooks', payload);
                    enqueueSnackbar('Webhook criado.');
                }
                await mutate();
                handleClose();
            } catch (error) {
                setErrors(error);
            }
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if (data?.id) {
            setValues({
                id: data.id,
                name: data.name || 'Eurobem webhook',
                url: data.url || defaultUrl,
                email: data.email || '',
                enabled: data.enabled !== false,
                interrupted: Boolean(data.interrupted),
                sendType: data.sendType || 'NON_SEQUENTIALLY',
                authToken: '',
                events: Array.isArray(data.events) ? data.events : [],
            });
        }
    }, [data, defaultUrl, setValues]);

    useEffect(() => {
        if (!values.id && defaultUrl && !values.url) {
            setFieldValue('url', defaultUrl);
        }
    }, [defaultUrl, setFieldValue, values.id, values.url]);

    const selectedSet = useMemo(() => new Set(values.events || []), [values.events]);

    const toggleEvent = eventName => {
        const next = new Set(selectedSet);
        if (next.has(eventName)) next.delete(eventName);
        else next.add(eventName);
        setFieldValue('events', Array.from(next));
    };

    const toggleGroup = (groupKey, checked) => {
        const groupEvents = groups[groupKey] || [];
        const next = new Set(selectedSet);
        groupEvents.forEach(ev => {
            if (checked) next.add(ev);
            else next.delete(ev);
        });
        setFieldValue('events', Array.from(next));
    };

    const isGroupFullySelected = groupKey => {
        const groupEvents = groups[groupKey] || [];
        if (!groupEvents.length) return false;
        return groupEvents.every(ev => selectedSet.has(ev));
    };

    const isGroupPartiallySelected = groupKey => {
        const groupEvents = groups[groupKey] || [];
        const count = groupEvents.filter(ev => selectedSet.has(ev)).length;
        return count > 0 && count < groupEvents.length;
    };

    const handleDelete = () => {
        setConfirmAction(() => async () => {
            try {
                await server.delete(`settings/asaas-webhooks/${values.id}`);
                enqueueSnackbar('Webhook removido.');
                await mutate();
                handleClose();
            } catch (error) {
                setErrors(error);
            }
            setConfirmOpen(false);
        });
        setConfirmOpen(true);
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">{values.id ? 'Editar webhook' : 'Novo webhook'}</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={2} component="form" id="asaas-webhook-form" onSubmit={handleSubmit}>
                        <TextField
                            label="Nome"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                        <TextField
                            label="URL"
                            name="url"
                            value={values.url}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText={
                                defaultUrl
                                    ? `URL desta API: ${defaultUrl}`
                                    : 'POST /webhooks/asaas no server-eurobem. Defina API_BASE_URL se a sugestão não aparecer.'
                            }
                        />
                        <TextField
                            label="E-mail de falhas"
                            name="email"
                            type="email"
                            value={values.email}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Token de autenticação (asaas-access-token)"
                            name="authToken"
                            value={values.authToken}
                            onChange={handleChange}
                            fullWidth
                            helperText={
                                values.id
                                    ? 'Deixe em branco para manter o token atual. Novo token: 32–255 caracteres.'
                                    : 'Opcional. Se informado, deve ter 32–255 caracteres.'
                            }
                        />
                        <TextField
                            select
                            label="Envio"
                            name="sendType"
                            value={values.sendType}
                            onChange={handleChange}
                            fullWidth
                        >
                            <MenuItem value="NON_SEQUENTIALLY">Não sequencial</MenuItem>
                            <MenuItem value="SEQUENTIALLY">Sequencial</MenuItem>
                        </TextField>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(values.enabled)}
                                        onChange={e => setFieldValue('enabled', e.target.checked)}
                                    />
                                }
                                label="Ativo"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(values.interrupted)}
                                        onChange={e => setFieldValue('interrupted', e.target.checked)}
                                    />
                                }
                                label="Fila interrompida"
                            />
                        </Stack>

                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Eventos
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1.5}>
                                Selecione os grupos de cobranças, assinaturas e checkout. Expanda para marcar eventos
                                individuais.
                            </Typography>
                            <Stack spacing={1}>
                                {GROUP_META.map(group => {
                                    const groupEvents = groups[group.key] || [];
                                    const fully = isGroupFullySelected(group.key);
                                    const partial = isGroupPartiallySelected(group.key);
                                    const selectedCount = groupEvents.filter(ev => selectedSet.has(ev)).length;
                                    const expanded = expandedGroup === group.key;
                                    return (
                                        <Box
                                            key={group.key}
                                            sx={{
                                                border: 1,
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 1.5,
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={fully}
                                                            indeterminate={partial}
                                                            onChange={e => toggleGroup(group.key, e.target.checked)}
                                                            disabled={!groupEvents.length}
                                                        />
                                                    }
                                                    label={
                                                        <Stack>
                                                            <Typography fontWeight={600}>{group.label}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {group.description} · {selectedCount}/{groupEvents.length}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => setExpandedGroup(expanded ? '' : group.key)}
                                                >
                                                    {expanded ? 'Ocultar' : 'Detalhar'}
                                                </Button>
                                            </Stack>
                                            <Collapse in={expanded}>
                                                <FormGroup sx={{ pl: 1, pt: 1, maxHeight: 220, overflow: 'auto' }}>
                                                    {groupEvents.map(eventName => (
                                                        <FormControlLabel
                                                            key={eventName}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selectedSet.has(eventName)}
                                                                    onChange={() => toggleEvent(eventName)}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                    {eventName}
                                                                </Typography>
                                                            }
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Box>
                        {values.id ? (
                            <Button color="error" onClick={handleDelete} disabled={isSubmitting}>
                                Remover
                            </Button>
                        ) : null}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={handleClose} disabled={isSubmitting}>
                            Cancelar
                        </Button>
                        <Button type="submit" form="asaas-webhook-form" variant="contained" disabled={isSubmitting}>
                            {values.id ? 'Salvar' : 'Criar'}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <ConfirmationModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmAction}
                title="Remover webhook"
                message="Tem certeza que deseja remover este webhook?"
            />
        </>
    );
}

export default function DialogAsaasWebhook({ mutate }) {
    const { open, id } = useDialog('#webhook');
    const { data: catalogResponse } = useFetch(open ? 'settings/asaas-webhooks/events' : null);
    const catalog = catalogResponse ?? null;

    if (open && id) {
        return <Edit id={id} mutate={mutate} catalog={catalog} />;
    }
    if (open) {
        return <Form data={null} mutate={mutate} catalog={catalog} />;
    }
    return null;
}
